// The header's primary menu — the inline bar's dropdowns (md and up) and the slide-in
// drawer that replaces the whole bar below md. Partials/Header.cshtml renders all of it
// from Menu.Primary / Menu.Secondary; this only drives their open state.
//
// The md–lg "Resources" fold is NOT here. Both shapes are in the DOM and Header.cshtml
// .scss shows exactly one, so nothing measures a width in script and a resize costs no
// reflow. This module treats Resources as just another dropdown.
//
// Styles live in Partials/Header.cshtml.scss. Initialised once from _Layout.cshtml.ts,
// alongside the language overlay.
//
// The drawer's Solutions accordion is deliberately NOT here: it is a real <details>, so
// it opens with no script and keeps working if this module never loads.
//
// The Solutions panel in the bar IS here, in two halves. Its visibility is the `hidden`
// attribute rather than a class, because the panel is a child of <header> and not of the
// menu item that opens it (Header.cshtml says why), so no CSS selector reaches it from
// the open <li>. Its rail is a tablist, and selecting a tab swaps which pane is shown.

// Below-md drawer + the modal scrim. `mask` on <body> is what paints #layout-mask and
// locks the page scroll — both already defined in _Layout.cshtml.scss, so the drawer
// itself only has to slide.
const openDrawer = (drawer: HTMLElement, toggle: HTMLElement | null): void => {
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mask');
    drawer.querySelector<HTMLButtonElement>('[data-menu-close]')?.focus();
};

const closeDrawer = (drawer: HTMLElement, toggle: HTMLElement | null, refocus = true): void => {
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mask');
    // The panel is now aria-hidden and (after the slide) visibility:hidden, so pull focus
    // back out to the control that opened it rather than leaving it in a hidden subtree.
    // Skipped on the resize path, where the user never asked to move.
    if (refocus) {
        toggle?.focus();
    }
};

export function initHeaderMenu(): void {
    const toggle = document.querySelector<HTMLButtonElement>('[data-menu-toggle]');
    const drawer = document.querySelector<HTMLElement>('#header-drawer');

    // Every group in the inline bar — one <li data-menu-group> per dropdown. Today that
    // is Solutions plus the Resources fold; only one of the two Resources shapes is ever
    // visible, and wiring the hidden one costs nothing.
    const groups = Array.from(document.querySelectorAll<HTMLElement>('[data-menu-group]'));
    const mega = document.querySelector<HTMLElement>('[data-mega-panel]');

    const closeGroups = (except?: HTMLElement): void => {
        groups.forEach((group) => {
            if (group === except) return;
            group.classList.remove('open');
            group.querySelector<HTMLButtonElement>('[data-menu-trigger]')
                ?.setAttribute('aria-expanded', 'false');
        });
        // Removing .open cannot hide the Solutions panel — it is not inside the <li> that
        // carries the class — so it is hidden here instead, unless the group being spared
        // is the one that owns it.
        if (!except?.hasAttribute('data-mega')) {
            mega?.setAttribute('hidden', '');
        }
    };

    groups.forEach((group) => {
        const trigger = group.querySelector<HTMLButtonElement>('[data-menu-trigger]');
        trigger?.addEventListener('click', (event) => {
            event.stopPropagation(); // don't let the document handler below close it again
            const willOpen = !group.classList.contains('open');
            closeGroups(group);      // one panel at a time
            group.classList.toggle('open', willOpen);
            trigger.setAttribute('aria-expanded', String(willOpen));
            if (group.hasAttribute('data-mega')) {
                mega?.toggleAttribute('hidden', !willOpen);
            }
        });
    });

    // The Solutions panel's rail. Selecting an industry swaps the pane beside it, and
    // selecting takes a CLICK — hovering the rail highlights the row but changes nothing.
    // That makes this the tablist pattern with manual activation: the arrow keys below
    // move focus along the rail, and Enter or Space (which a button reports as a click)
    // is what commits. Pointing at six industries in a row cannot flash six panes past
    // you on the way to the one you wanted.
    const tabs = mega ? Array.from(mega.querySelectorAll<HTMLButtonElement>('[role="tab"]')) : [];

    const selectTab = (tab: HTMLButtonElement): void => {
        tabs.forEach((other) => {
            const on = other === tab;
            other.setAttribute('aria-selected', String(on));
            // Roving tabindex: one stop for the whole rail, as a tablist should have.
            other.tabIndex = on ? 0 : -1;
            // A class, not the `hidden` attribute — the panes are stacked so the panel
            // keeps one height, and display: none would collapse that. See Header.cshtml.
            document.getElementById(other.getAttribute('aria-controls') ?? '')
                ?.classList.toggle('is-shown', on);
        });
    };

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectTab(tab));
        tab.addEventListener('keydown', (event) => {
            const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
            if (step === 0) return;
            event.preventDefault(); // arrows move the selection, they don't scroll the page
            tabs[(index + step + tabs.length) % tabs.length]?.focus();
        });
    });

    mega?.querySelector<HTMLButtonElement>('[data-mega-close]')?.addEventListener('click', () => {
        closeGroups();
        // Focus is inside a subtree that just became hidden; hand it back to the trigger.
        document.querySelector<HTMLButtonElement>('[data-mega] [data-menu-trigger]')?.focus();
    });

    // header.main slides out of view on scroll-down (the headroom logic in
    // _Layout.cshtml.ts), and the panel is its child, so it would ride off-screen with it.
    // Close instead. Scrolling INSIDE the panel does not reach window, so a tall panel
    // still scrolls normally.
    window.addEventListener('scroll', () => {
        if (mega && !mega.hasAttribute('hidden')) {
            closeGroups();
        }
    }, { passive: true });

    if (drawer) {
        toggle?.addEventListener('click', () => {
            if (drawer.classList.contains('open')) {
                closeDrawer(drawer, toggle);
            } else {
                openDrawer(drawer, toggle);
            }
        });

        drawer.querySelector<HTMLButtonElement>('[data-menu-close]')
            ?.addEventListener('click', () => closeDrawer(drawer, toggle));

        // Follow a link and the drawer has done its job — close it so the destination
        // isn't rendered behind a scrim if the browser restores this page from bfcache.
        drawer.querySelectorAll<HTMLAnchorElement>('a').forEach((link) =>
            link.addEventListener('click', () => closeDrawer(drawer, toggle, false)),
        );
    }

    // One outside-click handler for both surfaces: a press outside an open dropdown
    // closes it, and a press outside the open drawer closes that.
    //
    // The toggle has to be excluded explicitly, and it is load-bearing rather than
    // defensive: opening the drawer IS a click, and it bubbles to here a moment after
    // the panel opens — without the guard the drawer would shut on the very press that
    // opened it. Closing needs no such care, because the toggle's own handler has
    // already removed `.open` by the time this runs.
    //
    // Focus does return to the toggle here (unlike the link path, which is navigating
    // away). The press landed on the scrim, which takes no focus, so focus is still
    // sitting inside a panel that is about to be hidden.
    document.addEventListener('click', (event) => {
        const target = event.target as Element | null;
        // The Solutions panel sits outside every [data-menu-group], so it has to be spared
        // by name or the first click inside it would close it.
        if (!target?.closest('[data-menu-group]') && !target?.closest('[data-mega-panel]')) {
            closeGroups();
        }
        if (drawer?.classList.contains('open')
            && !target?.closest('#header-drawer')
            && !target?.closest('[data-menu-toggle]')) {
            closeDrawer(drawer, toggle);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closeGroups();
        if (drawer?.classList.contains('open')) {
            closeDrawer(drawer, toggle);
        }
    });

    // Widening past md swaps the drawer for the inline bar. The drawer is display:none
    // there so it cannot be seen either way, but <body> would keep the `mask` class —
    // a scrim over the page and a locked scroll with nothing left on screen to dismiss.
    // The query is md's ceiling and must track the `media-breakpoint-up(md)` rule that
    // hides the drawer in Header.cshtml.scss; the two are one decision in two places.
    window.addEventListener('resize', () => {
        if (drawer?.classList.contains('open') && !window.matchMedia('(max-width: 767.98px)').matches) {
            closeDrawer(drawer, toggle, false);
        }
    });
}
